import React, { useContext } from 'react';
import { useBuilderImages } from '../../context/BuilderImageContext';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Typography } from '@milanlaser/mlcomponents';
import type {
    FormData,
    SubmitArgs,
    ConsultFormV2Props,
    MilanBackgroundColors,
    MilanGradientColors,
} from '@milanlaser/mlcomponents';
import './HeroSections.styles.css';
import { BuilderComponentState } from 'components/builder/types';
import { BrandPartner } from 'utils/ssr';
import { GlobalSiteDataContext } from '../../../../context';
import { transformStoresForConsultForm } from '../../../../utils/general/transformStoresForConsultForm';
import { useBrandPartnerFormSubmission } from '../../../../hooks/useBrandPartnerFormSubmission';
import { FormSuccess } from '../../../../components/forms/shared';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import BrandPartnerForm from '../form/BrandPartner';

const styles = {
    image: {
        objectFit: 'cover',
        objectPosition: 'right center',
        width: '100%',
        height: '100%',
    } as React.CSSProperties,

    backgrounds: {
        section: (color: string) => ({ backgroundColor: color }),
        content: (color: string) => ({ backgroundColor: color }),
    },
};

const commonClasses = {
    imageContainer: 'h-100 position-relative',
    contentWrapper: 'd-flex flex-column justify-content-center h-100',
};

interface BrandPartnerBuilderState extends BuilderComponentState {
    state: BuilderComponentState['state'] & {
        brandPartner: BrandPartner;
    };
}

interface BrandPartnerHeroProps {
    heroDesktopImage: string;
    heroTabletImage: string;
    heroMobileImage: string;
    heroMobileImageAlt: string;
    heroDesktopImageAlt: string;
    heroTabletImageAlt: string;
    isHeroImgOnRight: boolean;
    desktopImageBackgroundColor: string;
    desktopBackgroundColor: string;
    contentBackgroundColor: string;
    backgroundColor?: string;
    gradient?: string;
    subHeading?: string;
    subHeadingColor?: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    builderState?: BrandPartnerBuilderState;
}

export function BrandPartnerHero({
    heroDesktopImage,
    heroTabletImage,
    heroMobileImage,
    heroMobileImageAlt,
    heroDesktopImageAlt,
    heroTabletImageAlt,
    isHeroImgOnRight,
    backgroundColor,
    gradient,
    subHeading,
    subHeadingColor,
    desktopPaddingTop = 'large',
    desktopPaddingBottom = 'large',
    mobilePaddingTop = 'small',
    mobilePaddingBottom = 'small',
    ...props
}: BrandPartnerHeroProps) {
    const { getOptimizedImage } = useBuilderImages();

    const { builderState } = props;
    const influencerHandle = builderState?.state?.brandPartner?.handle || '';
    const couponCode = builderState?.state?.brandPartner?.couponCode || '';

    // Get optimized image data for each breakpoint
    const desktopImageInfo = heroDesktopImage ? getOptimizedImage(heroDesktopImage) : null;
    const tabletImageInfo = heroTabletImage ? getOptimizedImage(heroTabletImage) : null;
    const mobileImageInfo = heroMobileImage ? getOptimizedImage(heroMobileImage) : null;

    const { gatsbyImageData: desktopGatsbyImage } = useOptimizedImage(
        desktopImageInfo?.nodeId || '',
        heroDesktopImageAlt || 'Hero image'
    );
    const { gatsbyImageData: tabletGatsbyImage } = useOptimizedImage(
        tabletImageInfo?.nodeId || '',
        heroTabletImageAlt || 'Hero image'
    );
    const { gatsbyImageData: mobileGatsbyImage, altText } = useOptimizedImage(
        mobileImageInfo?.nodeId || '',
        heroMobileImageAlt || 'Hero image'
    );

    const siteData = useContext(GlobalSiteDataContext);
    const locations = transformStoresForConsultForm(siteData.milanStores);

    const { externalValidation, handleValidationRequest, handleFormSubmission, isFormSubmissionSuccess, formType } =
        useBrandPartnerFormSubmission({
            couponCode: couponCode,
            recaptchaAction: 'corporateConsult',
        });

    const backgroundColorClass = processBackgroundAndGradientColorMap({
        backgroundColor: (backgroundColor as MilanBackgroundColors) || 'default',
        gradientColor: (gradient as MilanGradientColors) || 'default',
    });

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const defaultFormProps: ConsultFormV2Props = {
        locations,
        buttonText: 'Reserve Your Spot',
        claimOfferButtonText: 'Claim Your Offer',
        persistLocation: true,
        showCopy: true,
        externalValidation,
        onValidationRequest: handleValidationRequest,
        onSubmit: (formData: FormData, args: SubmitArgs) => handleFormSubmission(formData, args),
        fieldConfigs: {
            first_name: {
                id: 'first_name',
                name: 'first_name',
                ariaLabel: 'Enter first name',
                placeholder: 'First Name',
            },
            last_name: {
                id: 'last_name',
                name: 'last_name',
                ariaLabel: 'Enter last name',
                placeholder: 'Last Name',
            },
            email: {
                id: 'email',
                name: 'email',
                ariaLabel: 'Enter email address',
                placeholder: 'Email',
            },
            phone: {
                id: 'phone',
                name: 'phone',
                ariaLabel: 'Enter phone number',
                placeholder: 'Phone Number',
            },
            location: {
                id: 'location',
                name: 'location',
                ariaLabel: 'Select a location',
                placeholder: 'Location',
            },
        },
    };

    if (isFormSubmissionSuccess && formType === 'partial') {
        return <FormSuccess />;
    }

    return (
        // Main container with desktop background color
        <section
            className={`position-relative overflow-hidden px-0 sm:px-6 ${backgroundColorClass} ${topPaddingClasses} ${bottomPaddingClasses}`}
            id="reserve"
        >
            {/* Main content container using Bootstrap grid */}
            <div className="container-fluid">
                <div className={`row ${isHeroImgOnRight ? 'flex-row-reverse' : ''}`}>
                    <div className="text-center flex lg:hidden flex-col items-center px-12">
                        {
                            <Typography variant={'h1'} tag={'h2'}>
                                Exclusive for {influencerHandle} followers
                            </Typography>
                        }
                        {
                            <Typography variant={'pSubhead'} tag={'p'} className={`${subHeadingColor}`}>
                                {subHeading}
                            </Typography>
                        }
                    </div>
                    {/* Image Column */}
                    <div className="col-12 col-lg-6 px-0">
                        <div className={commonClasses.imageContainer}>
                            <div className="hidden xl:block h-100 hero-image-container">
                                {desktopGatsbyImage ? (
                                    <GatsbyImage
                                        image={desktopGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroDesktopImage}
                                        alt={heroDesktopImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>

                            {/* Tablet - show only on md to lg */}
                            <div className="hidden lg:block xl:hidden h-100">
                                {tabletGatsbyImage ? (
                                    <GatsbyImage
                                        image={tabletGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroTabletImage}
                                        alt={heroTabletImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>

                            {/* Mobile - show only on < md */}
                            <div className="block lg:hidden h-100">
                                {mobileGatsbyImage ? (
                                    <GatsbyImage
                                        image={mobileGatsbyImage}
                                        alt={altText}
                                        style={styles.image}
                                        className="img-fluid"
                                    />
                                ) : (
                                    <img
                                        src={heroMobileImage}
                                        alt={heroMobileImageAlt}
                                        style={styles.image}
                                        className="img-fluid"
                                        loading="eager"
                                        decoding="async"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Column */}
                    <div
                        className={`col-12 col-lg-6 flex flex-col items-center gap-6 xxl:pr-32 ${backgroundColorClass}`}
                    >
                        {/* Desktop Content with right padding */}
                        <div className="text-center hidden lg:flex flex-col items-center">
                            {
                                <Typography variant={'h1'} tag={'h2'}>
                                    Exclusive for {influencerHandle} followers
                                </Typography>
                            }
                            {
                                <Typography variant={'pSubhead'} tag={'p'} className={`${subHeadingColor}`}>
                                    {subHeading}
                                </Typography>
                            }
                        </div>
                        {/* <div className="max-w-[593px] hidden lg:flex w-full"> */}
                        <div className="lg:max-w-[593px] hidden lg:flex w-full">
                            <BrandPartnerForm props={{ ...defaultFormProps }} />
                        </div>
                        {/* Mobile/Tablet Content */}
                        <div className={`flex lg:hidden h-100 px-2 ${backgroundColorClass}`}>
                            <BrandPartnerForm props={{ ...defaultFormProps, isMobileView: true }} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
